import VueWrapper from '@/components/core/Vue/vue.wrapper';
import ClientFooterComponent from '@/components/vuetify/client-footer/client-footer.component';
import {PricingPlan} from '@/globals';
import {PlanApiService} from '@/sdk/api-services';
import {PaymentApiService} from '@/sdk/api-services/payment.api';
import {PlanModel} from '@/sdk/models/payment/payment.models';
import {Component, Watch} from 'vue-property-decorator';

@Component({
    components: {
        ClientFooterComponent,
    },
})
export default class SubscriptionPlanComponent extends VueWrapper {
    exchangeRate: number = 1350;
    exchangeRateDate: string | null = null;
    isLoadingRate: boolean = false;

    selectedPlanId: string | null = null;

    includeCircuit: boolean = false;
    extraUsers: number = 0;

    public isLoadingPlans: boolean = false;

    private planApi: PlanApiService = new PlanApiService();
    private paymentApi: PaymentApiService = new PaymentApiService();

    public plans: PricingPlan[] = [];

    private readonly enterprisePlan: PricingPlan = {
        id: 'enterprise',
        name: 'Enterprise',
        equipmentLimit: 'Unlimited',
        includedUsers: 0,
        priceUsd: 0,
        isEnterprise: true,
        features: ['All Standard Features', 'Custom Limits', 'Dedicated Support', 'Negotiable Add-ons'],
    };

    get currentPlan(): PricingPlan | undefined {
        return this.plans.find(p => p.id === this.selectedPlanId);
    }

    get isEnterpriseSelected(): boolean {
        return this.currentPlan?.isEnterprise || false;
    }

    get totalUsersCount(): number {
        if (!this.currentPlan || this.isEnterpriseSelected) return 0;
        const base = this.currentPlan?.includedUsers ?? 0;
        return base + this.extraUsers;
    }

    get subtotalUsd(): number {
        if (!this.currentPlan || this.isEnterpriseSelected) return 0;
        const planPrice = this.currentPlan.priceUsd;
        const circuitPrice = this.includeCircuit ? this.currentPlan.circuitModulePriceUsd ?? 0 : 0;
        return planPrice + circuitPrice;
    }

    get extraUserCostUsd(): number {
        const priceUsd = this.currentPlan?.additionalUserPriceUsd ?? 0;
        return this.extraUsers * priceUsd;
    }

    get estimatedTotalUsd(): number {
        return this.subtotalUsd + this.extraUserCostUsd;
    }

    get estimatedTotalKrw(): number {
        return this.estimatedTotalUsd * this.exchangeRate;
    }

    get subtotalKrw(): number {
        return this.subtotalUsd * this.exchangeRate;
    }

    get extraUserCostKrw(): number {
        return this.extraUserCostUsd * this.exchangeRate;
    }

    public async mounted() {
        await this.loadPlans();
        await this.loadExchangeRate();
    }

    private async loadExchangeRate() {
        this.isLoadingRate = true;
        try {
            const data = await this.paymentApi.getExchangeRate();
            const rate = data?.rate;
            if (typeof rate === 'number') {
                this.exchangeRate = rate;
                this.exchangeRateDate = data?.date ?? null;
            }
        } catch (error) {
            // Keep the default exchange rate if the API fails.
        } finally {
            this.isLoadingRate = false;
        }
    }

    private async loadPlans() {
        this.isLoadingPlans = true;
        try {
            const accountId = this.UserSessionSrv.Session?.selectedAccountId ?? this.UserSessionSrv.Session?.accountId ?? null;
            const plans = accountId ? await this.planApi.getAvailablePlans(accountId) : await this.planApi.getPublicPlans();
            const mapped = plans
                .map(plan => this.mapPlan(plan))
                .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER));
            this.plans = [...mapped, this.enterprisePlan];

            if (!this.selectedPlanId || !this.plans.find(p => p.id === this.selectedPlanId)) {
                const firstPublicPlan = mapped[0];
                this.selectedPlanId = firstPublicPlan?.id ?? this.enterprisePlan.id;
            }
        } catch (error) {
            this.plans = [this.enterprisePlan];
            this.selectedPlanId = this.enterprisePlan.id;
        } finally {
            this.isLoadingPlans = false;
        }
    }

    private mapPlan(plan: PlanModel): PricingPlan {
        const settings = plan.settings ?? {};
        const features = settings.features ?? {};
        const featureLabels = Object.keys(features)
            .filter(key => features[key])
            .map(key => this.formatFeatureLabel(key));

        return {
            id: plan.id,
            name: plan.name,
            equipmentLimit: settings.limits?.maxLocations ?? 0,
            includedUsers: settings.limits?.maxUsers ?? 0,
            priceUsd: plan.priceUsd,
            periodDays: plan.periodDays ?? 30,
            sortOrder: plan.sortOrder ?? 0,
            isEnterprise: false,
            features: featureLabels,
            additionalUserPriceUsd: settings.pricing?.additionalUserPriceUsd ?? 0,
            circuitModulePriceUsd: settings.addons?.electricCircuitModule?.priceUsd ?? 0,
        };
    }

    private formatFeatureLabel(value: string): string {
        return value
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^\w/, c => c.toUpperCase())
            .trim();
    }

    formatPrice(value: number, currency: 'USD' | 'KRW'): string {
        return new Intl.NumberFormat(currency === 'KRW' ? 'ko-KR' : 'en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(value);
    }

    formatKrw(value: number): string {
        return this.formatPrice(value, 'KRW');
    }

    selectPlan(planId: string) {
        this.selectedPlanId = planId;
        if (planId === 'enterprise') {
            this.includeCircuit = false;
            this.extraUsers = 0;
        }
    }

    @Watch('selectedPlanId')
    onPlanChange(newVal: string) {
        if (newVal === 'enterprise') {
            this.includeCircuit = false;
        }
    }

    proceedToCheckout() {
        if (!this.selectedPlanId || this.isEnterpriseSelected) {
            return;
        }

        const queryParams = {
            planId: this.selectedPlanId,
            includeCircuit: this.includeCircuit.toString(),
            extraUsers: this.extraUsers.toString(),
        };

        this.$router.push({
            name: 'Checkout',
            query: queryParams,
        });
    }
}
